import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface CurrencyInputProps {
  value?: number | null | undefined;
  onChange?: ((value: number | null) => void) | undefined;
  label?: string | undefined;
  error?: string | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
}

function formatCurrency(value: number | null): string {
  if (value == null || isNaN(value)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function parseCurrency(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const value = parseInt(digits, 10) / 100;
  return isNaN(value) ? null : value;
}

export function CurrencyInput({
  value,
  onChange,
  label,
  error,
  placeholder = 'R$ 0,00',
  className,
}: CurrencyInputProps) {
  const [display, setDisplay] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDisplay(formatCurrency(value ?? null));
    }
  }, [value, focused]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseCurrency(e.target.value);
      setDisplay(formatCurrency(parsed));
      onChange?.(parsed);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    if (!display || display === 'R$ 0,00') {
      setDisplay('');
    }
  }, [display]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    setDisplay(formatCurrency(value ?? null));
  }, [value]);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mono uppercase tracking-wider text-[12px] text-muted-foreground mb-2 block">
          {label}
        </label>
      )}
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(
          'w-full rounded-lg border border-border bg-card px-3 py-3 font-mono text-[12px] tabular-nums text-foreground outline-none transition-colors focus:border-foreground',
          error && 'border-destructive',
        )}
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
